/**
 * Shared mechanics for edit-mode region wrappers (EditableSlotGroup,
 * EditableCollectionBinding): hover tracking with scroll dismiss,
 * capture-phase click routing that defers to nested editables, and the
 * portaled hover label. Visual specifics stay at the call sites.
 */

import {
  useRef,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';

export interface UseEditableRegionOptions {
  /** Edit mode is on and the region should intercept interaction. */
  enabled: boolean;
  /** Nested editables inside the region that keep their own click targets. */
  nestedEditableSelector: string;
  onSelect: (region: HTMLElement) => void;
}

export function useEditableRegion({
  enabled,
  nestedEditableSelector,
  onSelect,
}: UseEditableRegionOptions) {
  const regionRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;
      e.stopPropagation();
      setIsHovered(true);
    },
    [enabled],
  );

  const onMouseLeave = useCallback(() => {
    if (!enabled) return;
    setIsHovered(false);
  }, [enabled]);

  // Dismiss the hover portal on scroll so fixed-position labels don't drift.
  useEffect(() => {
    if (!enabled || !isHovered) return;
    const dismiss = () => setIsHovered(false);
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', dismiss, { capture: true });
  }, [enabled, isHovered]);

  const onClickCapture = useCallback(
    (e: MouseEvent) => {
      if (!enabled) return;
      const region = e.currentTarget as HTMLElement;
      const nested = (e.target as HTMLElement).closest(nestedEditableSelector);
      if (nested && region.contains(nested) && nested !== region) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      (e.nativeEvent as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();
      onSelect(region);
    },
    [enabled, nestedEditableSelector, onSelect],
  );

  return {
    regionRef,
    isHovered,
    regionProps: { onMouseEnter, onMouseLeave, onClickCapture },
  };
}

/** Fixed-position label portaled above a region; caller supplies the visual style. */
export function EditableRegionHoverLabel({
  rect,
  style,
  children,
}: {
  rect: { top: number; left: number };
  style?: CSSProperties;
  children: ReactNode;
}) {
  return createPortal(
    <span
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        transform: 'translateY(-100%)',
        color: '#fff',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        ...style,
      }}
    >
      {children}
    </span>,
    document.body,
  );
}

export const editableRegionStyles = `
.vibesite-editable-region[data-vibesite-region-hovered="true"] {
  outline: 2px dashed rgba(119, 59, 255, 0.7) !important;
  outline-offset: 4px !important;
}
.vibesite-editable-region {
  user-select: none;
}
`;
